import { useState } from 'react';
import { Link } from 'react-router';
import {
  ArrowsClockwise,
  CaretRight,
  Check,
  Clock,
  Copy,
  CurrencyDollar,
  DiceFive,
  Eye,
  Lock,
  LockOpen,
  MagnifyingGlass,
  Megaphone,
  Plus,
  Trash,
  Users,
  X,
} from '@phosphor-icons/react';
import { formatPoints, formatSideLine, isFavorite as isFavoriteSide } from '@pickem/shared';
import type {
  CommishHomeScreenProps,
  CommishMembersScreenProps,
  CommishReplaceScreenProps,
  CommishResultsScreenProps,
  CommishSlateScreenProps,
  FeedPanelProps,
} from '../../screens/contracts';
import type { ResetLinkDto } from '@pickem/shared';
import { formatKickoff } from '../PickCard';
import {
  Avatar,
  Button,
  Card,
  cx,
  EmptyState,
  ErrorState,
  Field,
  InlineNotice,
  Input,
  LevelTag,
  MatchupRow,
  SectionLabel,
  Select,
  Skeleton,
  SkeletonList,
  Stat,
  TeamMark,
} from '../ui';

// ---------------------------------------------------------------------------
// Commissioner dashboard
// ---------------------------------------------------------------------------

export function CommishHome({
  isLoading,
  isError,
  error,
  retry,
  overview,
  slateHref,
  resultsHref,
  membersHref,
  broadcastHref,
}: CommishHomeScreenProps) {
  if (isError && error) return <ErrorState message={error} onRetry={retry} />;
  if (isLoading || !overview) return <SkeletonList rows={4} height="h-24" />;

  const week = overview.week;

  return (
    <div className="space-y-8">
      <div>
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-field-500">
          Commissioner
        </div>
        <h1 className="mt-1.5 text-3xl font-semibold tracking-tighter">Run the league</h1>
      </div>

      {/* Current week status */}
      {week ? (
        <Card className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-field-500">
                Current week
              </div>
              <div className="mt-1 text-xl font-semibold tracking-tight">{week.label}</div>
            </div>
            <StatusChip status={week.slateStatus} />
          </div>

          <div className="mt-6 grid grid-cols-3 gap-4">
            <Stat value={`${week.gameCount}/${week.requiredGames}`} label="Games" />
            <Stat value={overview.playersComplete} label="All in" accent />
            <Stat value={overview.playersNotStarted} label="Not started" />
          </div>

          {week.firstKickoffAt && (
            <div className="mt-5 flex items-center gap-2 border-t border-line pt-4 font-mono text-[12px] text-field-400">
              <Clock size={14} />
              First lock {formatKickoff(week.firstKickoffAt)}
            </div>
          )}

          {overview.playersPartial > 0 && (
            <div className="mt-3 font-mono text-[12px] text-push">
              {overview.playersPartial} player{overview.playersPartial === 1 ? '' : 's'} part-way
              through
            </div>
          )}
        </Card>
      ) : (
        <EmptyState
          title="No published week"
          description="Generate a slate and publish it so your league can start picking."
          action={
            <Link to={slateHref}>
              <Button>Build a slate</Button>
            </Link>
          }
        />
      )}

      {/* Actions — zig-zag rows rather than three equal cards. */}
      <div className="space-y-2.5">
        <ActionRow
          href={slateHref}
          icon={<DiceFive size={18} />}
          title="Slate generator"
          detail={
            overview.draftWeeks.length > 0
              ? `${overview.draftWeeks.length} draft week${overview.draftWeeks.length === 1 ? '' : 's'} waiting`
              : 'Generate, reroll and publish a week'
          }
          urgent={overview.draftWeeks.length > 0}
        />
        <ActionRow
          href={resultsHref}
          icon={<Check size={18} />}
          title="Results and grading"
          detail={
            overview.ungradedGames > 0
              ? `${overview.ungradedGames} game${overview.ungradedGames === 1 ? '' : 's'} finished but not graded`
              : 'Everything is graded'
          }
          urgent={overview.ungradedGames > 0}
        />
        <ActionRow
          href={membersHref}
          icon={<Users size={18} />}
          title="Members and invites"
          detail={`${overview.memberCount} in the league`}
        />
        <ActionRow
          href={broadcastHref}
          icon={<Megaphone size={18} />}
          title="Broadcasts"
          detail="Send the league a notification you wrote"
        />
      </div>
    </div>
  );
}

function ActionRow({
  href,
  icon,
  title,
  detail,
  urgent,
}: {
  href: string;
  icon: React.ReactNode;
  title: string;
  detail: string;
  urgent?: boolean;
}) {
  return (
    <Link to={href} className="block">
      <Card interactive className="flex items-center gap-3.5 p-4 active:scale-[0.99]">
        <span
          className={cx(
            'grid size-10 shrink-0 place-items-center rounded-lg',
            urgent ? 'bg-signal/15 text-signal' : 'bg-field-800 text-field-300',
          )}
        >
          {icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-field-100">{title}</div>
          <div className={cx('mt-0.5 text-[12px]', urgent ? 'text-signal' : 'text-field-500')}>
            {detail}
          </div>
        </div>
        <CaretRight size={16} className="shrink-0 text-field-600" />
      </Card>
    </Link>
  );
}

function StatusChip({ status }: { status: string }) {
  const tones: Record<string, string> = {
    draft: 'border-line bg-field-800 text-field-400',
    published: 'border-signal/30 bg-signal/10 text-signal',
    in_progress: 'border-push/30 bg-push/10 text-push',
    completed: 'border-win/30 bg-win/10 text-win',
  };
  return (
    <span
      className={cx(
        'shrink-0 rounded-md border px-2 py-1 font-mono text-[10px] uppercase tracking-wider',
        tones[status] ?? tones.draft,
      )}
    >
      {status.replace('_', ' ')}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Slate generator — the commissioner's centrepiece
// ---------------------------------------------------------------------------

/**
 * Generating a week should feel like dealing cards: one press produces five games, each
 * with its own Keep / Reroll / Replace controls, and Publish is the deliberate final act.
 *
 * The confirmation dialog is driven by the server: it refuses a destructive edit and
 * returns the number of picks at stake, which becomes the text of the prompt. The
 * commissioner is never asked a vague "are you sure".
 */
export function CommishSlate({
  isLoading,
  isError,
  error,
  retry,
  week,
  weeks,
  selectedWeekId,
  onSelectWeek,
  lastReport,
  busy,
  actionError,
  onGenerate,
  onRerollAll,
  onRerollOne,
  onRemove,
  onEditSpread,
  onPublish,
  onUnpublish,
  onSetLock,
  replaceHrefFor,
  addGameHref,
  confirmation,
  onConfirm,
  onCancelConfirmation,
  publishWarnings,
  audit,
  requiredGames,
  feed,
}: CommishSlateScreenProps) {
  const [showAudit, setShowAudit] = useState(false);
  const [editingSpread, setEditingSpread] = useState<string | null>(null);
  const [spreadDraft, setSpreadDraft] = useState('');

  if (isError && error) return <ErrorState message={error} onRetry={retry} />;
  if (isLoading || !week) return <SkeletonList rows={5} height="h-28" />;

  const games = week.games.filter((g) => g.status !== 'voided');
  const isDraft = week.week.slateStatus === 'draft';
  const isFull = games.length >= requiredGames;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-field-500">
            Slate generator
          </div>
          <h1 className="mt-1.5 text-3xl font-semibold tracking-tighter">{week.week.label}</h1>
        </div>
        <StatusChip status={week.week.slateStatus} />
      </div>

      <div className="taste-scroll-x -mx-4 flex gap-1.5 px-4 pb-1">
        {[...weeks]
          .sort((a, b) => a.weekNumber - b.weekNumber)
          .map((w) => (
            <button
              key={w.id}
              onClick={() => onSelectWeek(w.id)}
              className={cx(
                'shrink-0 rounded-lg px-3 py-2 font-mono text-xs font-medium tabular-nums transition-all active:scale-[0.96]',
                w.id === selectedWeekId
                  ? 'bg-signal text-ink'
                  : w.slateStatus === 'draft'
                    ? 'border border-dashed border-line text-field-500'
                    : 'bg-field-800 text-field-300',
              )}
            >
              W{w.weekNumber}
            </button>
          ))}
      </div>

      {actionError && <InlineNotice tone="error">{actionError}</InlineNotice>}
      {publishWarnings.length > 0 && (
        <InlineNotice tone="warn">
          <ul className="space-y-0.5">
            {publishWarnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </InlineNotice>
      )}

      <FeedPanel {...feed} isDraft={isDraft} />

      {/* Generation controls ------------------------------------------------ */}
      {isDraft && (
        <Card className="p-4">
          {games.length === 0 ? (
            // An empty slate offers both routes as equals. Random is the fast path, but
            // hand-picking is a first-class way to build a week, not a repair for a draw
            // you disliked — so it is a button here, not something you reach by rerolling.
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <Button size="lg" onClick={onGenerate} loading={busy.generate} full>
                <DiceFive size={19} weight="fill" />
                Draw {requiredGames} at random
              </Button>
              <Link to={addGameHref}>
                <Button size="lg" variant="outline" full>
                  <MagnifyingGlass size={17} weight="bold" />
                  Pick them myself
                </Button>
              </Link>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" onClick={onRerollAll} loading={busy.rerollAll}>
                <ArrowsClockwise size={16} weight="bold" />
                Reroll all
              </Button>
              {!isFull && (
                <>
                  <Button variant="outline" onClick={onGenerate} loading={busy.generate}>
                    <DiceFive size={16} weight="fill" />
                    Fill {requiredGames - games.length} at random
                  </Button>
                  <Link to={addGameHref}>
                    <Button variant="outline">
                      <Plus size={16} weight="bold" />
                      Add a game
                    </Button>
                  </Link>
                </>
              )}
              <Button
                onClick={() => onPublish(false)}
                loading={busy.publish}
                disabled={!isFull}
                className="ml-auto"
              >
                Publish week
              </Button>
            </div>
          )}

          {lastReport && (
            <div className="mt-3.5 border-t border-line pt-3.5 font-mono text-[11px] leading-relaxed text-field-500">
              Drew {lastReport.drawn}:{' '}
              {lastReport.buckets
                .map((b) => `${b.drawn} ${b.level.toUpperCase()} from ${b.eligible} eligible`)
                .join(' · ')}
              {lastReport.exclusionsRelaxed && (
                <span className="text-push"> · pool too small to avoid repeats</span>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Published controls -------------------------------------------------- */}
      {!isDraft && (
        <Card className="flex flex-wrap items-center gap-2 p-4">
          <Button variant="outline" size="sm" onClick={() => onUnpublish(false)}>
            Back to draft
          </Button>
          {week.week.picksLockedOverride === true ? (
            <Button variant="outline" size="sm" onClick={() => onSetLock('unlock')}>
              <LockOpen size={15} />
              Release lock
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => onSetLock('lock')}>
              <Lock size={15} />
              Lock all picks
            </Button>
          )}
          {week.week.picksLockedOverride === false && (
            <span className="font-mono text-[11px] text-push">Forced open</span>
          )}
        </Card>
      )}

      {/* The slate ---------------------------------------------------------- */}
      {games.length === 0 ? (
        <EmptyState
          title="No games yet"
          description={`Draw ${requiredGames} eligible games at random, or search the schedule and choose them yourself. Either way you can reroll, swap or re-price anything before publishing.`}
          icon={<DiceFive size={30} />}
          action={
            <Link to={addGameHref}>
              <Button variant="outline">
                <MagnifyingGlass size={16} weight="bold" />
                Browse the schedule
              </Button>
            </Link>
          }
        />
      ) : (
        <div className="space-y-2.5">
          {games.map((game, index) => (
            <Card
              key={game.id}
              className="taste-rise overflow-hidden"
              style={{ '--index': index } as React.CSSProperties}
            >
              <div className="flex items-center gap-2.5 border-b border-line px-3.5 py-2">
                <span className="grid size-5 place-items-center rounded bg-field-800 font-mono text-[10px] font-semibold text-field-400">
                  {game.slot}
                </span>
                <LevelTag level={game.level} />
                <span className="truncate font-mono text-[11px] text-field-500">
                  {formatKickoff(game.kickoffAt)}
                </span>
                <span className="ml-auto shrink-0 font-mono text-[9px] uppercase tracking-[0.14em] text-field-600">
                  {game.selectionSource.replace('commissioner_', '')}
                </span>
              </div>

              <MatchupRow
                className="px-3.5 py-3"
                size={28}
                home={{
                  team: game.home.team,
                  lineLabel: game.home.lineLabel,
                  isFavorite: game.home.isFavorite,
                }}
                away={{
                  team: game.away.team,
                  lineLabel: game.away.lineLabel,
                  isFavorite: game.away.isFavorite,
                }}
              />

              {editingSpread === game.id ? (
                <div className="flex items-center gap-2 border-t border-line bg-field-900 px-3.5 py-2.5">
                  <span className="font-mono text-[11px] text-field-500">
                    {game.home.team.abbreviation}
                  </span>
                  <Input
                    value={spreadDraft}
                    onChange={(e) => setSpreadDraft(e.target.value)}
                    className="h-9 w-24 font-mono"
                    placeholder="-4.5"
                    inputMode="decimal"
                  />
                  <Button
                    size="sm"
                    onClick={() => {
                      const parsed = Number(spreadDraft);
                      if (Number.isFinite(parsed)) onEditSpread(game.id, parsed);
                      setEditingSpread(null);
                    }}
                  >
                    Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingSpread(null)}>
                    <X size={15} />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-1 border-t border-line px-2 py-1.5">
                  <SlateAction
                    onClick={() => onRerollOne(game.id)}
                    loading={busy.rerollOne === game.id}
                    icon={<ArrowsClockwise size={14} weight="bold" />}
                    label="Reroll"
                    disabled={!isDraft}
                  />
                  <Link to={replaceHrefFor(game.id)} className="flex-1">
                    <SlateAction icon={<MagnifyingGlass size={14} weight="bold" />} label="Replace" full />
                  </Link>
                  <SlateAction
                    onClick={() => {
                      setEditingSpread(game.id);
                      setSpreadDraft(String(game.contestSpreadHome));
                    }}
                    icon={<span className="font-mono text-[11px]">±</span>}
                    label="Spread"
                  />
                  <SlateAction
                    onClick={() => onRemove(game.id)}
                    loading={busy.removing === game.id}
                    icon={<Trash size={14} />}
                    label="Remove"
                    danger
                  />
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Voided games stay visible — history must never silently lose a game. */}
      {week.games.some((g) => g.status === 'voided') && (
        <div className="space-y-2">
          <SectionLabel>Pulled from this week</SectionLabel>
          {week.games
            .filter((g) => g.status === 'voided')
            .map((game) => (
              <Card key={game.id} className="px-4 py-3 opacity-60">
                <div className="text-[13px] text-field-300">
                  {game.away.team.shortName} at {game.home.team.shortName}
                </div>
                <div className="mt-1 font-mono text-[11px] text-field-500">
                  {game.voidReason ?? 'Voided'} · picks kept and scored as void
                </div>
              </Card>
            ))}
        </div>
      )}

      {/* Audit */}
      <div className="border-t border-line pt-5">
        <button
          onClick={() => setShowAudit((v) => !v)}
          className="flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-field-500 hover:text-field-300"
        >
          <Eye size={13} />
          {showAudit ? 'Hide' : 'Show'} activity log ({audit.length})
        </button>
        {showAudit && (
          <div className="mt-3 divide-y divide-line border-t border-line">
            {audit.map((event) => (
              <div key={event.id} className="flex items-baseline gap-3 py-2">
                <span className="shrink-0 font-mono text-[10px] uppercase tracking-wider text-field-400">
                  {event.eventType.replace(/_/g, ' ')}
                </span>
                <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-field-600">
                  {event.actorName ?? 'system'}
                </span>
                <span className="shrink-0 font-mono text-[10px] text-field-600">
                  {new Date(event.createdAt).toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirmation, driven by the server's own numbers ------------------- */}
      {confirmation && (
        <div className="fixed inset-0 z-50 grid place-items-end bg-field-950/80 backdrop-blur-sm sm:place-items-center">
          <div className="taste-pop w-full max-w-md rounded-t-3xl border border-line bg-field-850 p-6 sm:rounded-card">
            <h3 className="text-lg font-semibold tracking-tight">Careful — picks are involved</h3>
            <p className="mt-2.5 text-sm leading-relaxed text-field-300">{confirmation.message}</p>
            <p className="mt-3 font-mono text-[11px] leading-relaxed text-field-500">
              Nothing is deleted. Affected picks are kept on record and scored as void, and
              those players are excluded from the denominator so nobody is penalised.
            </p>
            <div className="mt-6 flex gap-2">
              <Button variant="outline" onClick={onCancelConfirmation} full>
                Cancel
              </Button>
              <Button variant="danger" onClick={onConfirm} full>
                Go ahead
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The upstream-feed panel.
 *
 * Two buttons, and the difference between them is the whole point of the component. The
 * schedule is free, so it reads as a plain refresh. Spreads are metered against an
 * allowance that does not come back, so the button says what it will cost, the balance
 * sits next to it, and a refresh inside the freshness window declines to spend rather than
 * silently burning a credit to return the same numbers.
 *
 * The frozen-line note is here rather than in the docs because this is the screen where
 * the question occurs to somebody: pressing "pull spreads" while a week is published looks
 * like it might move the lines the league already picked against. It cannot — a contest is
 * graded against the spread copied onto its slate at selection — and saying so at the
 * moment of hesitation is worth more than saying it in a README.
 */
function FeedPanel({
  quota,
  isSyncingSchedule,
  isPullingSpreads,
  lastOdds,
  scheduleNote,
  error,
  onSyncSchedule,
  onPullSpreads,
  isDraft,
}: FeedPanelProps & { isDraft: boolean }) {
  const remaining = quota?.remaining ?? null;
  const blocked = quota !== null && !quota.canSpend;
  // A skip is not a failure, but it is the case where somebody presses again and again
  // wondering why nothing changed, so it earns an explicit override.
  const wasSkippedForFreshness = Boolean(
    lastOdds?.levels.some((l) => l.skipped && l.linesUpdated === 0 && l.note?.includes('old')),
  );

  return (
    <Card className="space-y-3 p-4">
      <SectionLabel
        action={
          remaining !== null && (
            <span
              className={cx(
                'font-mono text-[11px] tabular-nums',
                blocked ? 'text-loss' : remaining < 100 ? 'text-push' : 'text-field-500',
              )}
            >
              {remaining} credits left
            </span>
          )
        }
      >
        Upstream feeds
      </SectionLabel>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Button variant="outline" onClick={onSyncSchedule} loading={isSyncingSchedule} full>
          <ArrowsClockwise size={16} weight="bold" />
          Pull schedule
        </Button>
        <Button
          variant="outline"
          onClick={() => onPullSpreads(false)}
          loading={isPullingSpreads}
          disabled={blocked}
          full
        >
          <CurrencyDollar size={16} weight="bold" />
          Pull spreads · 2 credits
        </Button>
      </div>

      <div className="font-mono text-[11px] leading-relaxed text-field-500">
        Schedule, kickoffs and scores are free. Spreads cost one credit per pool and are
        never pulled on their own — nothing on this screen spends unless you press it.
        {quota?.lastSuccessAt && <> Spreads last pulled {relativeTime(quota.lastSuccessAt)}.</>}
      </div>

      {scheduleNote && <InlineNotice tone="info">{scheduleNote}</InlineNotice>}

      {lastOdds && (
        <div className="space-y-1.5 border-t border-line pt-3">
          <div className="font-mono text-[11px] text-field-400">
            {lastOdds.creditsSpent === 0
              ? 'No credits spent.'
              : `${lastOdds.creditsSpent} credit${lastOdds.creditsSpent === 1 ? '' : 's'} spent.`}{' '}
            {lastOdds.levels
              .map((l) => `${l.level.toUpperCase()} ${l.linesUpdated} lines`)
              .join(' · ')}
          </div>
          {lastOdds.levels
            .filter((l) => l.note)
            .map((l) => (
              <div key={l.level} className="font-mono text-[11px] text-push">
                {l.level.toUpperCase()}: {l.note}
              </div>
            ))}
          {lastOdds.warnings.map((warning) => (
            <div key={warning} className="font-mono text-[11px] text-loss">
              {warning}
            </div>
          ))}
          {wasSkippedForFreshness && !blocked && (
            <Button size="sm" variant="ghost" onClick={() => onPullSpreads(true)}>
              Pull anyway
            </Button>
          )}
        </div>
      )}

      {error && <InlineNotice tone="error">{error}</InlineNotice>}

      {!isDraft && (
        <div className="flex items-start gap-2 border-t border-line pt-3 font-mono text-[11px] leading-relaxed text-field-600">
          <Lock size={13} className="mt-0.5 shrink-0" />
          <span>
            This week is published, so its lines are frozen. Pulling spreads updates the
            market for future slates and cannot change a game anyone has already picked.
          </span>
        </div>
      )}
    </Card>
  );
}

/** Coarse "3 hours ago". Precision past the hour is noise for a weekly pick'em. */
function relativeTime(iso: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (minutes < 2) return 'just now';
  if (minutes < 60) return `${minutes} minutes ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/**
 * The reset link, shown once.
 *
 * Deliberately loud and deliberately dismissible. It is a bearer token — anyone holding it
 * can set that member's password — so it should be copied, sent, and cleared off the screen
 * rather than left sitting in a tab. It is not stored anywhere the commissioner can go back
 * and find it either; losing it just means minting another.
 */
function ResetLinkPanel({ link, onDismiss }: { link: ResetLinkDto; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);

  return (
    <Card className="border-signal/30 bg-signal/[0.06] p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-field-100">
            Reset link for {link.displayName}
          </div>
          <p className="mt-1 text-[12px] leading-relaxed text-field-400">
            Send this to them however you normally talk. They choose their own password —
            you never see it. Good until {formatExpiry(link.expiresAt)}, and only once.
          </p>
        </div>
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          className="grid size-7 shrink-0 place-items-center rounded-lg text-field-500 hover:bg-field-800 hover:text-field-100"
        >
          <X size={15} />
        </button>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <code className="min-w-0 flex-1 truncate rounded-lg border border-line bg-field-900 px-3 py-2 font-mono text-[11px] text-field-300">
          {link.url}
        </code>
        <Button
          size="sm"
          onClick={() => {
            void navigator.clipboard.writeText(link.url).then(
              () => setCopied(true),
              // Clipboard access can be refused; saying so beats a button that lies.
              () => setCopied(false),
            );
          }}
        >
          {copied ? <Check size={14} weight="bold" /> : <Copy size={14} />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </Card>
  );
}

function formatExpiry(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function SlateAction({
  onClick,
  icon,
  label,
  loading,
  danger,
  disabled,
  full,
}: {
  onClick?: () => void;
  icon: React.ReactNode;
  label: string;
  loading?: boolean;
  danger?: boolean;
  disabled?: boolean;
  full?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={cx(
        'flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-2 font-mono text-[10px] uppercase tracking-[0.1em]',
        'transition-all duration-200 active:scale-[0.96] disabled:opacity-35 disabled:active:scale-100',
        danger ? 'text-loss hover:bg-loss/10' : 'text-field-400 hover:bg-field-800 hover:text-field-100',
        full && 'w-full',
      )}
    >
      {loading ? <ArrowsClockwise size={14} className="animate-spin" /> : icon}
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Replacement browser
// ---------------------------------------------------------------------------

export function CommishReplace({
  isLoading,
  isError,
  error,
  retry,
  target,
  results,
  isFetching,
  filters,
  onFiltersChange,
  onChoose,
  onCancel,
  isSubmitting,
  actionError,
  conferences,
}: CommishReplaceScreenProps) {
  if (isError && error) return <ErrorState message={error} onRetry={retry} />;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-field-500">
            {target ? 'Replacing' : 'Add a game'}
          </div>
          <h1 className="mt-1.5 truncate text-2xl font-semibold tracking-tighter">
            {target
              ? `${target.away.team.shortName} at ${target.home.team.shortName}`
              : 'Browse the schedule'}
          </h1>
        </div>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <X size={16} />
        </Button>
      </div>

      {actionError && <InlineNotice tone="error">{actionError}</InlineNotice>}

      {/* Filters */}
      <Card className="space-y-3 p-4">
        <div className="relative">
          <MagnifyingGlass
            size={16}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-field-500"
          />
          <Input
            value={filters.q}
            onChange={(e) => onFiltersChange({ q: e.target.value })}
            placeholder="Search team, city or abbreviation"
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Select
            value={filters.level}
            onChange={(e) => onFiltersChange({ level: e.target.value as 'nfl' | 'cfb' | '' })}
          >
            <option value="">All levels</option>
            <option value="nfl">NFL</option>
            <option value="cfb">College</option>
          </Select>
          <Select
            value={filters.conference}
            onChange={(e) => onFiltersChange({ conference: e.target.value })}
          >
            <option value="">All conferences</option>
            {conferences.map((conference) => (
              <option key={conference} value={conference}>
                {conference}
              </option>
            ))}
          </Select>
          <Input
            type="date"
            value={filters.date}
            onChange={(e) => onFiltersChange({ date: e.target.value })}
            className="h-11 w-auto"
          />
        </div>
      </Card>

      {isLoading && !results ? (
        <SkeletonList rows={6} height="h-16" />
      ) : !results || results.items.length === 0 ? (
        <EmptyState
          title="No games match"
          description="Try widening the filters. Games that already started, have no line, or are already on this slate are not offered."
          icon={<MagnifyingGlass size={30} />}
        />
      ) : (
        <div className={cx('space-y-2', isFetching && 'opacity-60 transition-opacity')}>
          {results.items.map((game) => (
            <button
              key={game.id}
              onClick={() => game.eligible && onChoose(game.id)}
              disabled={!game.eligible || isSubmitting}
              className="block w-full text-left"
            >
              <Card
                interactive={game.eligible}
                className={cx('p-3.5', game.eligible ? 'active:scale-[0.99]' : 'opacity-45')}
              >
                <MatchupRow
                  size={28}
                  home={{
                    team: game.home,
                    lineLabel:
                      game.marketSpreadHome === null
                        ? null
                        : formatSideLine(game.marketSpreadHome, 'home'),
                    isFavorite:
                      game.marketSpreadHome !== null && isFavoriteSide(game.marketSpreadHome, 'home'),
                  }}
                  away={{
                    team: game.away,
                    lineLabel:
                      game.marketSpreadHome === null
                        ? null
                        : formatSideLine(game.marketSpreadHome, 'away'),
                    isFavorite:
                      game.marketSpreadHome !== null && isFavoriteSide(game.marketSpreadHome, 'away'),
                  }}
                />
                <div className="mt-2 flex items-center gap-2 font-mono text-[11px] text-field-500">
                  <LevelTag level={game.level} />
                  {formatKickoff(game.kickoffAt)}
                  {game.marketSpreadHome === null && (
                    <span className="text-field-600">No line yet</span>
                  )}
                </div>
                {!game.eligible && (
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-wider text-push">
                    {game.ineligibleReasons.map((r) => r.replace(/_/g, ' ')).join(' · ')}
                  </div>
                )}
              </Card>
            </button>
          ))}

          {results.hasMore && (
            <Button
              variant="outline"
              full
              onClick={() => onFiltersChange({ page: filters.page + 1 })}
            >
              Load more
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Results entry
// ---------------------------------------------------------------------------

export function CommishResults({
  isLoading,
  isError,
  error,
  retry,
  week,
  weeks,
  selectedWeekId,
  onSelectWeek,
  onSaveResult,
  onSettle,
  isSettling,
  settleReport,
  savingGameId,
  actionError,
}: CommishResultsScreenProps) {
  const [drafts, setDrafts] = useState<Record<string, { home: string; away: string }>>({});

  if (isError && error) return <ErrorState message={error} onRetry={retry} />;
  if (isLoading || !week) return <SkeletonList rows={5} height="h-24" />;

  const games = week.games.filter((g) => g.status !== 'voided');
  const ungraded = games.filter((g) => g.result === 'pending').length;

  return (
    <div className="space-y-5">
      <div>
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-field-500">
          Results
        </div>
        <h1 className="mt-1.5 text-3xl font-semibold tracking-tighter">{week.week.label}</h1>
      </div>

      <div className="taste-scroll-x -mx-4 flex gap-1.5 px-4 pb-1">
        {[...weeks]
          .sort((a, b) => a.weekNumber - b.weekNumber)
          .map((w) => (
            <button
              key={w.id}
              onClick={() => onSelectWeek(w.id)}
              className={cx(
                'shrink-0 rounded-lg px-3 py-2 font-mono text-xs font-medium tabular-nums transition-all',
                w.id === selectedWeekId ? 'bg-signal text-ink' : 'bg-field-800 text-field-300',
              )}
            >
              W{w.weekNumber}
            </button>
          ))}
      </div>

      {actionError && <InlineNotice tone="error">{actionError}</InlineNotice>}

      {/*
        The one-press path, above the manual entry it replaces. It pulls the scoreboard and
        settles everything that has finished; the boxes below stay for the cases a feed
        cannot cover — a game it never carried, or a score it got wrong.

        No cost warning and no confirmation, unlike the spread pull on the slate screen.
        This reads the free scoreboard feed, and it grades against the line frozen at
        publication, so there is nothing here to be careful about.
      */}
      <Card className="p-4">
        <div className="flex items-center gap-4">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-field-100">Settle from scores</div>
            <div className="mt-0.5 text-[12px] leading-relaxed text-field-500">
              {ungraded === 0
                ? 'Every game on this slate is settled.'
                : `Pulls the latest scores and grades the ${ungraded} game${ungraded === 1 ? '' : 's'} still open. Free — this is the scoreboard feed, not the odds feed.`}
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="shrink-0"
            loading={isSettling}
            onClick={onSettle}
          >
            <ArrowsClockwise size={14} weight="bold" />
            {isSettling ? 'Settling' : 'Settle'}
          </Button>
        </div>

        {settleReport && (
          <div className="mt-3 border-t border-line pt-3">
            <InlineNotice
              tone={
                settleReport.warnings.length > 0
                  ? 'warn'
                  : settleReport.graded > 0
                    ? 'success'
                    : 'info'
              }
            >
              {settleReport.graded > 0
                ? `Settled ${settleReport.graded} game${settleReport.graded === 1 ? '' : 's'}.`
                : 'Nothing to settle yet — no game has a final score the feed agrees on.'}
              {settleReport.gamesUpdated > 0 &&
                ` Refreshed ${settleReport.gamesUpdated} score${settleReport.gamesUpdated === 1 ? '' : 's'}.`}
              {settleReport.warnings.map((warning) => (
                <span key={warning} className="mt-1 block">
                  {warning}
                </span>
              ))}
            </InlineNotice>
          </div>
        )}
      </Card>

      <InlineNotice tone="info">
        Scores are graded against the spread stored when the week was published, not
        today&apos;s line. A game you enter by hand below stays as you set it — a later
        settle will not overwrite it.
      </InlineNotice>

      <div className="space-y-2.5">
        {games.map((game) => {
          const draft = drafts[game.id] ?? {
            away: game.away.score?.toString() ?? '',
            home: game.home.score?.toString() ?? '',
          };
          const graded = game.result !== 'pending';

          return (
            <Card key={game.id} className="p-4">
              <div className="flex items-center gap-2 font-mono text-[11px] text-field-500">
                <span>{game.slot}</span>
                <LevelTag level={game.level} />
                <span className="truncate">{formatKickoff(game.kickoffAt)}</span>
                {graded && (
                  <span className="ml-auto shrink-0 rounded bg-win/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-win">
                    {game.result.replace('_', ' ')}
                  </span>
                )}
              </div>

              <div className="mt-3 space-y-2">
                {(['away', 'home'] as const).map((side) => {
                  const data = side === 'away' ? game.away : game.home;
                  return (
                    <div key={side} className="flex items-center gap-3">
                      <TeamMark team={data.team} size={28} />
                      <span className="min-w-0 flex-1 truncate text-[13px] text-field-200">
                        {data.team.shortName}{' '}
                        <span className="font-mono text-field-500">{data.lineLabel}</span>
                      </span>
                      <Input
                        value={draft[side]}
                        onChange={(e) =>
                          setDrafts((prev) => ({
                            ...prev,
                            [game.id]: { ...draft, [side]: e.target.value },
                          }))
                        }
                        inputMode="numeric"
                        placeholder="—"
                        className="h-10 w-16 text-center font-mono"
                      />
                    </div>
                  );
                })}
              </div>

              <Button
                size="sm"
                variant="outline"
                full
                className="mt-3"
                loading={savingGameId === game.id}
                disabled={draft.home === '' || draft.away === ''}
                onClick={() =>
                  onSaveResult({
                    slateGameId: game.id,
                    homeScore: Number(draft.home),
                    awayScore: Number(draft.away),
                    status: 'final',
                  })
                }
              >
                {graded ? 'Update result' : 'Save final score'}
              </Button>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Members
// ---------------------------------------------------------------------------

export function CommishMembers({
  isLoading,
  isError,
  error,
  retry,
  members,
  invites,
  onChangeRole,
  onChangeStatus,
  onCreateInvite,
  onRevokeInvite,
  isCreatingInvite,
  actionError,
  joinUrlFor,
  onResetPassword,
  resettingMembershipId,
  resetLink,
  onDismissResetLink,
}: CommishMembersScreenProps) {
  const [copied, setCopied] = useState<string | null>(null);

  if (isError && error) return <ErrorState message={error} onRetry={retry} />;
  if (isLoading) return <SkeletonList rows={6} height="h-16" />;

  const activeInvites = invites.filter((i) => i.active);

  return (
    <div className="space-y-8">
      <div>
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-field-500">
          League
        </div>
        <h1 className="mt-1.5 text-3xl font-semibold tracking-tighter">Members</h1>
      </div>

      {actionError && <InlineNotice tone="error">{actionError}</InlineNotice>}

      {resetLink && <ResetLinkPanel link={resetLink} onDismiss={onDismissResetLink} />}

      <section className="space-y-3">
        <SectionLabel>{members.length} in the league</SectionLabel>
        <div className="divide-y divide-line border-t border-line">
          {members.map((member) => (
            <div key={member.membershipId} className="flex items-center gap-3 py-3">
              <Avatar
                userId={member.userId}
                displayName={member.displayName}
                url={member.avatarUrl}
                size={36}
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-sm text-field-100">{member.displayName}</span>
                  {member.isMe && (
                    <span className="shrink-0 font-mono text-[9px] tracking-wider text-signal">
                      YOU
                    </span>
                  )}
                </div>
                <div className="mt-0.5 font-mono text-[11px] text-field-500">
                  {formatPoints(member.points)} pts · {member.picksMade}/{member.picksAvailable}{' '}
                  picks
                </div>
              </div>

              <Select
                value={member.role}
                onChange={(e) =>
                  onChangeRole(member.membershipId, e.target.value as 'player' | 'commissioner')
                }
                className="h-9 shrink-0 text-xs"
                disabled={member.isMe}
              >
                <option value="player">Player</option>
                <option value="commissioner">Commish</option>
              </Select>
              {!member.isMe && (
                <>
                  <button
                    onClick={() => onResetPassword(member.membershipId)}
                    disabled={resettingMembershipId === member.membershipId}
                    title={`Create a password reset link for ${member.displayName}`}
                    className="shrink-0 rounded-lg px-2 py-1.5 font-mono text-[10px] uppercase tracking-wider text-field-500 transition-colors hover:bg-field-800 hover:text-signal disabled:opacity-50"
                  >
                    {resettingMembershipId === member.membershipId ? 'Making…' : 'Reset'}
                  </button>
                  <button
                    onClick={() =>
                      onChangeStatus(
                        member.membershipId,
                        member.status === 'active' ? 'removed' : 'active',
                      )
                    }
                    className="shrink-0 rounded-lg px-2 py-1.5 font-mono text-[10px] uppercase tracking-wider text-field-500 transition-colors hover:bg-field-800 hover:text-loss"
                  >
                    {member.status === 'active' ? 'Remove' : 'Restore'}
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="space-y-3 border-t border-line pt-8">
        <SectionLabel
          action={
            <Button size="sm" variant="outline" onClick={onCreateInvite} loading={isCreatingInvite}>
              New invite
            </Button>
          }
        >
          Invite codes
        </SectionLabel>

        {activeInvites.length === 0 ? (
          <EmptyState
            title="No active invites"
            description="Create a code and share it with whoever you want in the league."
          />
        ) : (
          <div className="space-y-2">
            {activeInvites.map((invite) => (
              <Card key={invite.id} className="flex items-center gap-3 p-3.5">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-sm font-semibold tracking-wider text-field-100">
                    {invite.code}
                  </div>
                  <div className="mt-0.5 font-mono text-[11px] text-field-500">
                    {invite.uses} use{invite.uses === 1 ? '' : 's'}
                    {invite.maxUses ? ` of ${invite.maxUses}` : ' · unlimited'}
                  </div>
                </div>
                <button
                  onClick={() => {
                    void navigator.clipboard.writeText(joinUrlFor(invite.code));
                    setCopied(invite.id);
                    setTimeout(() => setCopied(null), 2000);
                  }}
                  className="grid size-9 shrink-0 place-items-center rounded-lg text-field-400 transition-colors hover:bg-field-800 hover:text-field-100"
                  aria-label="Copy invite link"
                >
                  {copied === invite.id ? (
                    <Check size={16} weight="bold" className="text-win" />
                  ) : (
                    <Copy size={16} />
                  )}
                </button>
                <button
                  onClick={() => onRevokeInvite(invite.id)}
                  className="grid size-9 shrink-0 place-items-center rounded-lg text-field-500 transition-colors hover:bg-loss/10 hover:text-loss"
                  aria-label="Revoke invite"
                >
                  <Trash size={15} />
                </button>
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export { Skeleton, Field };
